import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ExternalLink, Settings2 } from 'lucide-react';
import { useSourcingMatches, useUpdateSourcingSettings } from '../../hooks/useApi';
import { useToast } from '../../contexts/ToastContext';
import { TuningBanner } from './TuningBanner';
import { RankChipBadge } from './RankChipBadge';
import { STAR_LABELS } from '../../constants/sourcing-star-bands';

interface Props {
  jobId: number;
  passiveSourcingEnabled: boolean;
  sourcingMinStars: number;
}

function StarRating({ stars }: { stars: number }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${n <= stars ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
          data-testid={n <= stars ? 'star-filled' : 'star-empty'}
        />
      ))}
    </span>
  );
}


export default function SuggestedCandidates({ jobId, passiveSourcingEnabled, sourcingMinStars }: Props) {
  const { showToast } = useToast();
  const [localEnabled, setLocalEnabled] = useState(passiveSourcingEnabled);
  const [localMinStars, setLocalMinStars] = useState(sourcingMinStars);

  const { data, isLoading } = useSourcingMatches(jobId, { limit: 20, minStars: localMinStars });
  const updateSettings = useUpdateSourcingSettings();

  const handleToggle = async (enabled: boolean) => {
    try {
      await updateSettings.mutateAsync({ jobId, data: { passiveSourcingEnabled: enabled } });
      setLocalEnabled(enabled);
      showToast(enabled ? 'Passive sourcing enabled' : 'Passive sourcing disabled', 'success');
    } catch {
      showToast('Failed to update settings', 'error');
    }
  };

  const handleMinStars = async (stars: number) => {
    try {
      await updateSettings.mutateAsync({ jobId, data: { sourcingMinStars: stars } });
      setLocalMinStars(stars);
      showToast('Minimum match updated', 'success');
    } catch {
      showToast('Failed to update settings', 'error');
    }
  };

  return (
    <div className="space-y-4">
      {/* Tuning entry point / persistent attribution (US-404) — only relevant
          once suggestions actually exist, so gated on the same localEnabled
          flag as the rest of this component's content.

          US-404 fix round: TuningBanner's `tuned` prop must reflect whether
          this job HAS a calibrated/applied spec (persistent), not
          `data.tuned` (whether THIS response's ranking happened to run
          spec-aware). `data.tuned` is false whenever the inference budget is
          exhausted, ranking degrades, or the route's catch-all fires — none
          of which mean the job reverted to uncalibrated, so deriving the
          banner from it would silently flip a calibrated job back to "rate
          12 candidates" on a request-by-request basis. `specVersion` is
          non-null exactly when an applied search spec exists, which is the
          right persistent signal. */}
      {/* US-510: also gated on `data`, matching SourcingPage. Without it this
          flashed the untuned "Rate 12 candidates" CTA on every cold load of an
          already-calibrated job, because `data?.specVersion` is undefined until
          the first response lands. `tunedAt`/`rankingApplied` now come from
          that response instead of being hardcoded — see SourcingPage. */}
      {localEnabled && data && (
        <TuningBanner
          jobId={jobId}
          tuned={data.specVersion != null}
          termCounts={{ mustHave: data.mustHaveCount, exclusions: data.exclusionCount }}
          tunedAt={data.tunedAt}
          rankingApplied={data.tuned}
        />
      )}

      {/* Settings bar */}
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
        <Settings2 className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        <label className="flex items-center gap-2 text-sm">
          <button
            role="switch"
            aria-checked={localEnabled}
            onClick={() => handleToggle(!localEnabled)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${localEnabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}
            disabled={updateSettings.isPending}
          >
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${localEnabled ? 'translate-x-4' : 'translate-x-1'}`} />
          </button>
          <span className="text-gray-700 dark:text-gray-300">Passive sourcing</span>
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          Min match:
          <select
            value={localMinStars}
            onChange={(e) => handleMinStars(parseInt(e.target.value, 10))}
            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1 text-xs"
            disabled={updateSettings.isPending}
          >
            {[5, 4, 3, 2, 1].map((s) => (
              <option key={s} value={s}>{STAR_LABELS[s]}</option>
            ))}
          </select>
        </label>
        <Link
          to={`/jobs/${jobId}/sourcing`}
          className="ml-auto text-xs text-blue-400 hover:text-blue-300"
        >
          View all →
        </Link>
      </div>

      {/* Content */}
      {!localEnabled ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          Enable passive sourcing above to start finding suggested candidates for this role.
        </div>
      ) : isLoading ? (
        <div role="status" className="animate-pulse space-y-3 px-1">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 space-y-1">
                <div className="h-3 w-40 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="h-3 w-10 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          ))}
        </div>
      ) : !data?.matches.length ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>No suggested candidates yet.</p>
          <p className="mt-1 text-xs">Sourcing runs nightly — check back tomorrow. Try lowering the minimum match threshold above.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {data.matches.map((match) => (
            <div key={match.candidateId} className="flex items-center gap-4 py-3 px-1">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/20 text-xs font-medium text-blue-400">
                {match.candidateName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <Link
                  to={`/people/${match.candidateId}`}
                  className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-blue-400 truncate block"
                >
                  {match.candidateName}
                </Link>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{match.candidateEmail}</p>
                {match.chips?.[0] && <RankChipBadge chip={match.chips[0]} />}
              </div>
              <StarRating stars={match.stars} />
              <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-right">
                {Math.round(match.score * 100)}%
              </span>
              {match.srCandidateId && (
                <a
                  href={`https://www.smartrecruiters.com/app/people/candidates/${match.srCandidateId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="View in SmartRecruiters"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
